<script setup>
import { ref, computed, watch, onMounted, onUnmounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'dashboard/composables/store';
import Draggable from 'dashboard/components/widgets/webphone/Draggable.vue';
import { useWhatsappCall } from 'dashboard/composables/useWhatsappCall';
import { useAlert } from 'dashboard/composables';
import { emitter } from 'shared/helpers/mitt';
import { useVoiceCallStatus } from 'dashboard/composables/useVoiceCallStatus';
import { startRingback, stopRing } from 'dashboard/helper/callRingtone';
import whatsappCalls from 'dashboard/api/whatsappCalls';

const { t } = useI18n();
const store = useStore();

// --- store ---
const call = computed(() => store.getters['whatsappCall/getCall']);
const uiFlags = computed(() => store.getters['whatsappCall/getUIFlags']);

// --- composable (OWNS the RTCPeerConnection) ---
const { applyAnswer, toggleMute, hangup, isMuted, cleanup } = useWhatsappCall();

// --- permission prompt ---
const showPermissionPrompt = ref(false);
const permissionConversationId = ref(null);

// --- call timer ---
const elapsedSeconds = ref(0);
const timer = ref(null);

const formattedDuration = computed(() => {
  const m = Math.floor(elapsedSeconds.value / 60);
  const s = elapsedSeconds.value % 60;
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
});

function stopTimer() {
  if (timer.value) {
    clearInterval(timer.value);
    timer.value = null;
  }
  elapsedSeconds.value = 0;
}

function startTimer() {
  stopTimer();
  elapsedSeconds.value = 0;
  timer.value = setInterval(() => {
    elapsedSeconds.value += 1;
  }, 1000);
}

watch(
  () => call.value?.status,
  status => {
    if (status === 'active') {
      stopRing();
      startTimer();
    } else if (!status || status === 'ended') {
      stopRing();
      stopTimer();
    } else {
      // calling / connecting — play the ringback tone
      startRingback();
    }
  },
  { immediate: true }
);

// --- status mapping: our statuses → useVoiceCallStatus expected values ---
// store statuses: null | 'calling' | 'connecting' | 'active' | 'ended'
const mappedStatus = computed(() => {
  const s = call.value?.status;
  if (s === 'active') return 'in-progress';
  if (s === 'ended') return 'completed';
  return 'ringing'; // 'calling', 'connecting', null → ringing
});

const { bubbleIconBg } = useVoiceCallStatus(mappedStatus, 'outbound');

// Status text using WHATSAPP_CALL i18n keys for precision
const statusLabel = computed(() => {
  const s = call.value?.status;
  if (s === 'calling') return t('CONVERSATION.WHATSAPP_CALL.CALLING');
  if (s === 'connecting') return t('CONVERSATION.WHATSAPP_CALL.CONNECTING');
  if (s === 'active') return t('CONVERSATION.WHATSAPP_CALL.IN_CALL');
  if (s === 'ended') return t('CONVERSATION.WHATSAPP_CALL.ENDED');
  return t('CONVERSATION.WHATSAPP_CALL.CALLING');
});

// --- hang up ---
async function onHangup() {
  const { conversationId, recordId } = call.value || {};
  try {
    await hangup(conversationId, recordId);
  } finally {
    // Always close the panel, even if the terminate request fails.
    store.dispatch('whatsappCall/reset');
  }
}

// --- request permission ---
async function requestPermission() {
  try {
    await whatsappCalls.requestPermission(permissionConversationId.value);
    useAlert(t('CONVERSATION.WHATSAPP_CALL.PERMISSION_SENT'));
    showPermissionPrompt.value = false;
  } catch {
    useAlert(t('CONVERSATION.WHATSAPP_CALL.ERROR_GENERIC'));
  }
}

// --- emitter handlers ---
function onAnswer({ sdp }) {
  applyAnswer(sdp);
}

function onTerminated() {
  cleanup();
  store.dispatch('whatsappCall/reset');
}

function onPermissionRequired({ conversationId }) {
  permissionConversationId.value = conversationId;
  showPermissionPrompt.value = true;
}

onMounted(() => {
  emitter.on('whatsapp_call:answer', onAnswer);
  emitter.on('whatsapp_call:terminated', onTerminated);
  emitter.on('whatsapp_call:permission_required', onPermissionRequired);
});

onUnmounted(() => {
  emitter.off('whatsapp_call:answer', onAnswer);
  emitter.off('whatsapp_call:terminated', onTerminated);
  emitter.off('whatsapp_call:permission_required', onPermissionRequired);
  stopRing();
  stopTimer();
});
</script>

<template>
  <Draggable :initial-x="50" :initial-y="50">
    <!-- Permission prompt — shown when 138006 error fired via emitter -->
    <div
      v-if="showPermissionPrompt"
      data-testid="permission-prompt"
      class="flex flex-col gap-3 w-[280px] bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl p-4 shadow-lg select-none"
    >
      <p class="text-sm font-semibold text-slate-800 dark:text-slate-100 m-0">
        {{ $t('CONVERSATION.WHATSAPP_CALL.PERMISSION_TITLE') }}
      </p>
      <p class="text-xs text-slate-600 dark:text-slate-300 m-0">
        {{ $t('CONVERSATION.WHATSAPP_CALL.PERMISSION_BODY') }}
      </p>
      <button
        data-testid="request-permission-btn"
        class="rounded-lg bg-woot-500 text-white text-xs font-medium px-4 py-2 hover:bg-woot-600 transition-colors"
        @click="requestPermission"
      >
        {{ $t('CONVERSATION.WHATSAPP_CALL.PERMISSION_SEND') }}
      </button>
    </div>

    <!-- Active call panel -->
    <div
      v-if="uiFlags.isOpen"
      data-testid="call-panel"
      class="flex flex-col h-[280px] w-[250px] bg-white dark:bg-slate-900 opacity-95 border border-slate-200 dark:border-slate-700 rounded-xl shadow-lg select-none cursor-grab"
    >
      <!-- Contact + status -->
      <div
        class="flex flex-col items-center justify-center flex-1 gap-1 px-4 pt-6"
      >
        <div
          class="w-10 h-10 rounded-full flex items-center justify-center mb-2"
          :class="bubbleIconBg"
        >
          <span class="i-ri-phone-fill text-white text-base" />
        </div>
        <p
          class="text-base font-semibold text-slate-800 dark:text-slate-100 m-0 text-center truncate max-w-full"
        >
          {{ call.contactName || call.phone }}
        </p>
        <p class="text-xs text-slate-500 dark:text-slate-400 m-0 text-center">
          {{ statusLabel }}
        </p>
        <p
          v-if="call.status === 'active'"
          class="text-lg font-mono font-medium text-slate-800 dark:text-slate-100 m-0 text-center tabular-nums"
        >
          {{ formattedDuration }}
        </p>
      </div>

      <!-- Controls -->
      <div class="flex justify-center items-center gap-5 pb-6">
        <!-- Mute / Unmute -->
        <button
          v-tooltip="
            isMuted
              ? $t('CONVERSATION.WHATSAPP_CALL.UNMUTE')
              : $t('CONVERSATION.WHATSAPP_CALL.MUTE')
          "
          data-testid="mute-btn"
          class="flex items-center justify-center w-10 h-10 rounded-full transition-colors"
          :class="
            isMuted
              ? 'bg-red-100 text-red-600 dark:bg-red-900 dark:text-red-300'
              : 'bg-slate-100 text-slate-700 dark:bg-slate-700 dark:text-slate-200'
          "
          @click="toggleMute"
        >
          <span
            class="text-base"
            :class="isMuted ? 'i-ri-mic-off-line' : 'i-ri-mic-line'"
          />
        </button>

        <!-- Hang up -->
        <button
          v-tooltip="$t('CONVERSATION.WHATSAPP_CALL.HANG_UP')"
          data-testid="hangup-btn"
          class="flex items-center justify-center w-10 h-10 rounded-full bg-red-500 text-white hover:bg-red-600 transition-colors"
          @click="onHangup"
        >
          <span class="text-base i-lucide-phone-off" />
        </button>
      </div>
    </div>
  </Draggable>
</template>
